import { Box, IconButton, Typography } from '@mui/material'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import LastPageIcon from '@mui/icons-material/LastPage'
import CfpSelect from './CfpSelect'
import { FS_MICRO, FS_SM, ICON_SM } from '../common/theme'
import RemoveIcon from "@mui/icons-material/Remove";


export const PM_PAGE_SIZE_OPTIONS = [20, 50, 100]

export default function ProjectPaginator({
  projectcount,
  page,
  pageSize,
  onPage,
  onPageSizeChange,
}: {
  projectcount: number
  page: number
  pageSize: number
  onPage: (p: number) => void
  onPageSizeChange: (size: number) => void
}) {
  if (projectcount === 0) return null
  const totalPages = Math.ceil(projectcount / pageSize)
  const pageStart = page * pageSize
  const pageEnd = Math.min(pageStart + pageSize, projectcount)
  const hasNext = page < totalPages - 1

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'end', borderColor: 'divider', px: 1, py: 0.75, gap: 1.5, flexShrink: 0 }}>

      {/* Page navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ fontSize: FS_MICRO, opacity: 0.6, whiteSpace: 'nowrap', mr: 0.5 }}>
          Projects {pageStart + 1}–{pageEnd} of {projectcount}
        </Typography>
        {(page > 0 || hasNext) && (
          <>
            <IconButton size="small" disabled={page === 0} onClick={() => onPage(0)} sx={{ p: 0.25 }}>
              <FirstPageIcon sx={{ fontSize: ICON_SM }} />
            </IconButton>
            <Typography variant="caption"
              onClick={page > 0 ? () => onPage(page - 1) : undefined}
              sx={{ fontSize: FS_SM, cursor: page > 0 ? 'pointer' : 'default', opacity: page > 0 ? 1 : 0.35, userSelect: 'none', px: 0.25 }}
            >
              Prev
            </Typography>
            {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i).map(pg => (
              <Box key={pg} onClick={() => onPage(pg)} sx={{
                minWidth: 22, height: 22, px: 0.25, borderRadius: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
                bgcolor: page === pg ? 'primary.main' : 'transparent',
                border: page === pg ? 'none' : '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: page === pg ? 'primary.main' : 'action.hover' },
              }}>
                <Typography variant="caption" sx={{ fontSize: FS_SM, fontWeight: page === pg ? 700 : 400, color: page === pg ? '#fff' : 'text.primary', userSelect: 'none' }}>
                  {pg + 1}
                </Typography>
              </Box>
            ))}
            <Typography variant="caption"
              onClick={hasNext ? () => onPage(page + 1) : undefined}
              sx={{ fontSize: FS_SM, cursor: hasNext ? 'pointer' : 'default', opacity: hasNext ? 1 : 0.35, userSelect: 'none', px: 0.25 }}
            >
              Next
            </Typography>
            <IconButton size="small" disabled={!hasNext} onClick={() => onPage(totalPages - 1)} sx={{ p: 0.25 }}>
              <LastPageIcon sx={{ fontSize: ICON_SM }} />
            </IconButton>
          </>
        )}

        {/* Page size selector */}
        <Box sx={{ ml: 1 }}>
          <CfpSelect<number>
            value={pageSize}
            onChange={onPageSizeChange}
            options={PM_PAGE_SIZE_OPTIONS.map(n => ({ value: n, label: `${n} / page` }))}
          />
        </Box>
      </Box>
    </Box>
  )
}
